import * as Typography from "../../Element/Typography";
import Button from "../../Element/Button";

import { ReactNode } from "react";
import styles from "./affiliatesproduct.module.css";
import ResponsiveImage from "../../Element/ResponsiveImage";
import cx from "classnames";

type dealsSource = {
  discountPriceLabel?: string;
  discountPrice?: string;
  originalPriceLabel?: string;
  originalPrice?: string;
  discountInPercentage?: string;
  link: string;
  dynamicPricing?: string;
  sourceName?: string;
};

type AffiliatesItemProduct = {
  title: string;
  image: {
    src: string;
    srcWidth: number;
    srcHeight: number;
    alt: string;
  };
  dealsSource?: dealsSource[];
  productInfo?: {
    discountText?: string;
    headerTitle?: string;
  };
  link?: string;
};

type AffiliatesProductCTA = {
  variant?: "primary" | "secondary";
  label: string;
  target?: string;
  buttonIcon: ReactNode;
};

type AffiliatesProductProps = {
  item: AffiliatesItemProduct;
  itemWidth?: number;
  itemHeight?: number;
  cta?: AffiliatesProductCTA;
  enforceDoublePriceLine?: boolean;
  options?: {
    sourceNameAsLabel?: boolean;
    discountPercentageBesidePrice?: boolean;
    multipleDeals?: boolean;
    hasHeaderTitle?: boolean;
    hasBadge?: boolean;
  };
};

const { Pagination } = Typography;
const { SubHeading4 } = Typography.Heading;

/**
 * AffiliatesProduct
 * Description:
 */
export default function AffiliatesProduct({
  item,
  itemWidth,
  cta,
  enforceDoublePriceLine,
  options,
}: AffiliatesProductProps): ReactNode {
  const firstDeal = item.dealsSource && item.dealsSource[0];
  const isPriceDisplay =
    firstDeal?.discountPrice !== undefined ||
    firstDeal?.originalPrice !== undefined;

  const discountPercentageLabel = firstDeal?.discountInPercentage &&
    firstDeal?.discountPrice && (
      <Pagination size="small">{firstDeal?.discountInPercentage}</Pagination>
    );

  return (
    <div
      className={styles.container}
      style={{
        width: itemWidth,
        // Needed for NaturalCarousel items
        minWidth: itemWidth,
      }}
      data-layout-position="affiliateproduct"
    >
      <a
        href={firstDeal ? firstDeal?.link : item.link}
        className={styles.linkContainer}
        target="_blank"
        rel="noopener nofollow"
      >
        <span className={styles.imageContainer}>
          {!options?.discountPercentageBesidePrice &&
            discountPercentageLabel && (
              <div className={styles.floatingContainer}>
                <span className={styles.floatingPercentageLabel}>
                  {discountPercentageLabel}
                </span>
              </div>
            )}

          <ResponsiveImage
            {...item.image}
            fill="contain"
            fillAspectRatio={1}
            displayWidth={180}
            loading={import.meta.env.VITE_SKIP_LAZY ? undefined : "lazy"}
          />
        </span>

        {options?.hasHeaderTitle && item.productInfo?.headerTitle && (
          <Typography.Label>{item.productInfo?.headerTitle}</Typography.Label>
        )}

        <span
          className={styles.titleContainer}
          data-layout-position="affiliateproductname"
        >
          <SubHeading4>{item.title}</SubHeading4>
        </span>

        {options?.hasBadge && item.productInfo?.discountText && (
          <div className={styles.badgeContainer}>
            <Typography.Label>
              {item.productInfo?.discountText}
            </Typography.Label>
          </div>
        )}
      </a>

      {isPriceDisplay !== false && (
        <div className={styles.pricingContainer}>
          <div className={styles.currentPrice}>
            {firstDeal?.discountPrice
              ? `${firstDeal?.discountPriceLabel} ${firstDeal?.discountPrice}`
              : `${firstDeal?.originalPrice}`}
          </div>

          {firstDeal?.discountPrice ? (
            <div
              className={cx(
                styles.originalPrice,
                options?.discountPercentageBesidePrice &&
                  styles.withDiscountPercentage,
              )}
            >
              {firstDeal?.originalPriceLabel}
              <s className={styles.originalPriceStrikethrough}>
                {firstDeal?.originalPrice}
              </s>
              {options?.discountPercentageBesidePrice &&
                discountPercentageLabel}
            </div>
          ) : enforceDoublePriceLine ? (
            <div className={styles.originalPrice}>&nbsp;</div>
          ) : null}
        </div>
      )}

      {item.dealsSource && item.dealsSource.length > 0 && (
        <div className={styles.sourceNameContainer}>
          {item.dealsSource.map((deal, index) => {
            return (
              cta && (
                <Button
                  key={index}
                  href={deal?.link}
                  variant={cta.variant || "secondary"}
                  icon={cta.buttonIcon}
                  iconPlacement="before"
                  target={cta.target ?? "_blank"}
                  rel="noopener nofollow"
                >
                  {options?.sourceNameAsLabel ? deal?.sourceName : cta.label}
                </Button>
              )
            );
          })}
        </div>
      )}
    </div>
  );
}
